// ---------------------------------------------------------------------------
// ReportsUploadPanel — central place inside Settings (الإعدادات) to upload
//   1. Finance monthly PDF reports (saved to db.finance_reports + db.pdf_storage)
//   2. Mystery Shopper PDF reports (saved to db.mystery_evaluations + db.pdf_storage)
// + lists all previously uploaded PDFs so the admin can download/delete them.
// ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./lib/api";
import PdfDropzone from "./PdfDropzone";
import PhotoCapture from "./PhotoCapture";
import DateField from "./components/DateField";

const MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ReportsUploadPanel({ isAdmin }) {
  const [files, setFiles] = useState([]);
  const [reloading, setReloading] = useState(false);

  const reload = useCallback(async () => {
    setReloading(true);
    try {
      const { data } = await api.get("/files");
      setFiles(data?.items || []);
    } catch (e) {
      // silent — list is best-effort
    }
    setReloading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="set-sec" data-testid="reports-upload-panel">
      <h4>📎 رفع التقارير (PDF)</h4>
      <p style={{ fontSize: 12, color: "var(--gy)", marginBottom: 14, lineHeight: 1.8 }}>
        ارفع هنا تقارير الإدارة المالية وتقارير العميل السرّي مرة واحدة — الذكاء الاصطناعي
        يستخرج البيانات تلقائياً، ويتم حفظ الملف الأصلي للاسترجاع لاحقاً من صفحاته الخاصة.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <FinanceUploadCard onDone={reload} />
        <MysteryUploadCard onDone={reload} />
      </div>

      <QualityUploadCard onDone={reload} />

      {/* Employee Excel upload removed — employees now sync from Malachite HCM
          via the "🔄 مزامنة من HR" button on the Employees page. */}

      <div style={{ marginTop: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--brown-deep)" }}>
            📂 الملفات المرفوعة ({files.length})
          </div>
          <button type="button" className="btn btn-outline" onClick={reload} disabled={reloading} data-testid="files-reload" style={{ fontSize: 11 }}>
            {reloading ? "⏳…" : "🔄 تحديث"}
          </button>
        </div>
        <FilesList files={files} onDeleted={reload} isAdmin={isAdmin} />
      </div>
    </div>
  );
}

function FinanceUploadCard({ onDone }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [phase, setPhase] = useState("regular");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!file) { setErr("اختر ملف PDF"); return; }
    if (file.size > 25 * 1024 * 1024) { setErr("الملف أكبر من 25 ميجا — الرجاء ضغطه أولاً"); return; }
    setBusy(true); setErr(""); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("year", String(year));
      fd.append("month", String(month));
      fd.append("period_phase", phase);
      fd.append("period_label", `${MONTHS[month - 1]} ${year}`);
      const { data } = await api.post("/finance/extract-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg(`✓ ${data?.saved_report?.period_label || "حفظ التقرير"} — استخراج ${Object.keys(data?.fields || {}).length} حقل`);
      setFile(null);
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل الرفع");
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 14 }} data-testid="finance-upload-card">
      <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
        💰 رفع تقرير مالي شهري
      </div>
      <p style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, lineHeight: 1.7 }}>
        قائمة الدخل / الميزانية الشهرية — يُحفظ في «الإدارة المالية» مع الملف الأصلي للتحميل لاحقاً.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
        <SmallField label="السنة" type="number" value={year} onChange={(v) => setYear(Number(v))} testid="fu-year" />
        <SmallSelect label="الشهر" value={month} onChange={(v) => setMonth(Number(v))} options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} testid="fu-month" />
        <SmallSelect label="المرحلة" value={phase} onChange={setPhase} options={[
          { value: "setup", label: "تجهيز" },
          { value: "formation", label: "تشكيل" },
          { value: "regular", label: "شهر كامل" },
        ]} testid="fu-phase" />
      </div>
      <PdfDropzone file={file} onFile={(f) => { setFile(f); setErr(""); setMsg(null); }} testid="fu-file" disabled={busy} />
      <button type="button" className="btn btn-primary" onClick={submit} disabled={!file || busy} data-testid="fu-submit" style={{ fontSize: 13, marginTop: 10, width: "100%", padding: "10px", fontWeight: 800 }}>
        {busy ? "⏳ يستخرج عبر AI…" : "📤 رفع وحفظ"}
      </button>
      {err && <div style={{ fontSize: 11.5, color: "var(--rd)", marginTop: 6 }} data-testid="fu-err">{err}</div>}
      {msg && <div style={{ fontSize: 11.5, color: "var(--success)", marginTop: 6 }} data-testid="fu-ok">{msg}</div>}
    </div>
  );
}

function MysteryUploadCard({ onDone }) {  const [file, setFile] = useState(null);
  const [visitDate, setVisitDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branch, setBranch] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!file) { setErr("اختر ملف PDF"); return; }
    if (file.size > 25 * 1024 * 1024) { setErr("الملف أكبر من 25 ميجا — الرجاء ضغطه أولاً"); return; }
    setBusy(true); setErr(""); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      if (visitDate) fd.append("visit_date", visitDate);
      if (branch) fd.append("branch", branch);
      const { data } = await api.post("/mystery/upload-pdf-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg(`✓ تقييم جديد — النتيجة ${data.overall_score}% · ${data.issues?.length || 0} ملاحظة`);
      setFile(null);
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل التحليل");
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 14 }} data-testid="mystery-upload-card">
      <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
        🕵️ رفع تقرير العميل السرّي
      </div>
      <p style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, lineHeight: 1.7 }}>
        PDF من شركة التقييم — يُستخرج تلقائياً ويُحفظ مع الملف الأصلي للتحميل من صفحة «العميل السرّي».
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
        <SmallField label="تاريخ الزيارة" type="date" value={visitDate} onChange={setVisitDate} testid="mu-date" />
        <SmallField label="الفرع" placeholder="مثل: العزيزية" value={branch} onChange={setBranch} testid="mu-branch" />
      </div>
      <PdfDropzone file={file} onFile={(f) => { setFile(f); setErr(""); setMsg(null); }} testid="mu-file" disabled={busy} />
      <button type="button" className="btn btn-primary" onClick={submit} disabled={!file || busy} data-testid="mu-submit" style={{ fontSize: 13, marginTop: 10, width: "100%", padding: "10px", fontWeight: 800 }}>
        {busy ? "⏳ يحلّل عبر AI…" : "📤 رفع وحفظ"}
      </button>
      {err && <div style={{ fontSize: 11.5, color: "var(--rd)", marginTop: 6 }} data-testid="mu-err">{err}</div>}
      {msg && <div style={{ fontSize: 11.5, color: "var(--success)", marginTop: 6 }} data-testid="mu-ok">{msg}</div>}
    </div>
  );
}

function FilesList({ files, onDeleted, isAdmin }) {  const grouped = useMemo(() => {
    const fin = files.filter((f) => f.owner_type === "finance_report");
    const ms = files.filter((f) => f.owner_type === "mystery_evaluation");
    const other = files.filter((f) => f.owner_type !== "finance_report" && f.owner_type !== "mystery_evaluation");
    return { fin, ms, other };
  }, [files]);

  if (!files.length) {
    return (
      <div style={{ fontSize: 12, color: "var(--muted)", padding: 14, textAlign: "center", border: "1px dashed var(--border)", borderRadius: 8 }}>
        لا توجد ملفات مرفوعة بعد.
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <FilesGroup title="💰 تقارير مالية" items={grouped.fin} onDeleted={onDeleted} isAdmin={isAdmin} />
      <FilesGroup title="🕵️ تقارير العميل السرّي" items={grouped.ms} onDeleted={onDeleted} isAdmin={isAdmin} />
      {grouped.other.length > 0 && (
        <FilesGroup title="📁 ملفات أخرى" items={grouped.other} onDeleted={onDeleted} isAdmin={isAdmin} />
      )}
    </div>
  );
}

function FilesGroup({ title, items, onDeleted, isAdmin }) {
  // Default to collapsed when the list is long — the user previously had a
  // 16-row "تقارير العميل السرّي" section stretching the entire Settings page.
  // We keep small lists (≤3) open since the cost of expanding is negligible.
  const [open, setOpen] = useState(items.length <= 3);
  if (!items.length) return null;
  return (
    <div data-testid={`files-group-${title}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: "var(--brown-deep)",
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 11, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }} aria-hidden>▶</span>
        <span>{title} ({items.length})</span>
        <span style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 500, marginInlineStart: 4 }}>
          {open ? "اضغط للإخفاء" : "اضغط للعرض"}
        </span>
      </button>
      {open && (
        <div style={{ display: "grid", gap: 6 }}>
          {items.map((f) => <FileRow key={f.id} f={f} onDeleted={onDeleted} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}

function FileRow({ f, onDeleted, isAdmin }) {
  const [busy, setBusy] = useState(false);
  const sizeKb = Math.round((f.size || 0) / 1024);
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download`;

  const triggerDownload = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(downloadUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = f.filename || `${f.id}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { /* noop */ }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`حذف الملف «${f.filename}»؟`)) return;
    setBusy(true);
    try {
      await api.delete(`/files/${f.id}`);
      onDeleted?.();
    } catch (e) { /* noop */ }
    setBusy(false);
  };

  return (
    <div className="card" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "8px 10px", fontSize: 12,
    }} data-testid={`file-row-${f.id}`}>
      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <span style={{ fontWeight: 700, color: "var(--brown-deep)" }}>{f.filename}</span>
        <span style={{ marginInlineStart: 8, color: "var(--muted)", fontSize: 11 }}>
          {sizeKb} KB · {(f.uploaded_at || "").slice(0, 10)} · {f.uploaded_by}
        </span>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button type="button" className="btn btn-outline" onClick={triggerDownload} disabled={busy} data-testid={`file-dl-${f.id}`} style={{ fontSize: 11 }}>
          📥 تنزيل
        </button>
        {isAdmin && (
          <button type="button" className="btn btn-outline" onClick={remove} disabled={busy} data-testid={`file-rm-${f.id}`} style={{ fontSize: 11, color: "var(--rd)" }}>
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

function SmallField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 700 }}>{label}</span>
      {type === "date" ? (
        <DateField value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} data-testid={testid} style={{ padding: 5, fontSize: 12 }} />
      ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="sim-input"
        style={{ padding: 5, fontSize: 12 }}
        data-testid={testid}
      />
      )}
    </label>
  );
}

function SmallSelect({ label, value, onChange, options, testid }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 700 }}>{label}</span>
      <select
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          // Preserve number type when option values are numeric
          const opt = options.find((o) => String(o.value) === v);
          onChange(opt ? opt.value : v);
        }}
        className="sim-input"
        style={{ padding: 5, fontSize: 12 }}
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

// EmployeesUploadCard — bulk-upload the staff roster (Excel xlsx) so the
// rest of the app can read salaries, IDs, phones, branches from one source.
function QualityUploadCard({ onDone }) {
  // v119.61: Mode toggle between PDF upload and Camera Capture (PWA).
  const [mode, setMode] = useState("pdf"); // "pdf" | "photos"
  const [file, setFile] = useState(null);
  const [photos, setPhotos] = useState([]); // File[]
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("الزيارة الأسبوعية");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr(""); setMsg(null);
    if (mode === "pdf") {
      if (!file) { setErr("اختر ملف PDF"); return; }
      if (file.size > 25 * 1024 * 1024) { setErr("الملف أكبر من 25 ميجا"); return; }
    } else {
      if (!photos.length) { setErr("التقط صورة واحدة على الأقل"); return; }
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (mode === "pdf") {
        fd.append("file", file, file.name);
      } else {
        photos.forEach((p, i) => fd.append("files", p, p.name || `photo_${i}.jpg`));
      }
      if (reportDate) fd.append("report_date", reportDate);
      if (reason)     fd.append("visit_reason", reason);
      const endpoint = mode === "pdf" ? "/quality/reports/upload-pdf" : "/quality/reports/upload-photos";
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const chCount = (data?.changes || []).length;
      setMsg(chCount > 0
        ? `✓ تم حفظ التقرير + إشعار الفريق بـ ${chCount} تغيير`
        : `✓ تم حفظ التقرير (لا تغييرات منذ آخر تقرير)`);
      setFile(null);
      setPhotos([]);
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل الرفع");
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 14, marginTop: 14 }} data-testid="quality-upload-card">
      <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
        ☕ رفع تقرير جودة القهوة
      </div>
      <p style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, lineHeight: 1.7 }}>
        تقرير موظف الجودة الأسبوعي — يستخرج الذكاء الاصطناعي الصفوف السبعة تلقائياً ويحفظ التقرير ويُشعر الفريق بأي تغيير في الطحنة أو المعايرة.
      </p>
      {/* Mode toggle */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <button type="button" onClick={() => setMode("pdf")} data-testid="qu-mode-pdf"
          style={{
            flex: 1, padding: "8px 12px", borderRadius: 8, cursor: "pointer",
            background: mode === "pdf" ? "var(--brown-deep)" : "transparent",
            color: mode === "pdf" ? "#fff" : "var(--brown-deep)",
            border: mode === "pdf" ? "1.5px solid var(--brown-deep)" : "1px solid var(--border)",
            fontSize: 12.5, fontWeight: 700,
          }}>📄 ملف PDF</button>
        <button type="button" onClick={() => setMode("photos")} data-testid="qu-mode-photos"
          style={{
            flex: 1, padding: "8px 12px", borderRadius: 8, cursor: "pointer",
            background: mode === "photos" ? "var(--brown-deep)" : "transparent",
            color: mode === "photos" ? "#fff" : "var(--brown-deep)",
            border: mode === "photos" ? "1.5px solid var(--brown-deep)" : "1px solid var(--border)",
            fontSize: 12.5, fontWeight: 700,
          }}>📷 تصوير بالكاميرا</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
        <SmallField label="تاريخ الزيارة" type="date" value={reportDate} onChange={setReportDate} testid="qu-date" />
        <SmallField label="سبب الزيارة" type="text" value={reason} onChange={setReason} testid="qu-reason" />
      </div>
      {mode === "pdf" ? (
        <PdfDropzone file={file} onFile={(f) => { setFile(f); setErr(""); setMsg(null); }} testid="qu-file" disabled={busy} />
      ) : (
        <PhotoCapture photos={photos} onChange={(arr) => { setPhotos(arr); setErr(""); setMsg(null); }}
          testid="qu-photos" disabled={busy} max={6} />
      )}
      <button type="button" className="btn btn-primary" onClick={submit}
        disabled={busy || (mode === "pdf" ? !file : photos.length === 0)}
        data-testid="qu-submit"
        style={{ fontSize: 13, marginTop: 10, width: "100%", padding: "10px", fontWeight: 800 }}>
        {busy ? "⏳ يستخرج عبر AI…" : (mode === "pdf"
          ? "📤 رفع PDF"
          : (photos.length === 0 ? "📷 ابدأ بالتقاط الصور" : `📤 رفع ${photos.length} صورة`))}
      </button>
      {err && <div style={{ fontSize: 11.5, color: "var(--rd)", marginTop: 6 }} data-testid="qu-err">{err}</div>}
      {msg && <div style={{ fontSize: 11.5, color: "var(--success)", marginTop: 6 }} data-testid="qu-ok">{msg}</div>}
    </div>
  );
}


function EmployeesUploadCard({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);

  // Pre-load the existing summary so the admin sees what's currently stored.
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/employees/summary"); setSummary(data); }
      catch (_e) { /* silent — first-time empty */ }
    })();
  }, [msg]);

  const submit = async () => {
    if (!file) { setErr("اختر ملف Excel"); return; }
    if (file.size > 10 * 1024 * 1024) { setErr("الملف أكبر من 10 ميجا"); return; }
    setBusy(true); setErr(""); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post("/employees/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg(`✓ تم تحميل ${data.count} موظف · إجمالي الرواتب ${(data.summary?.total_salary_gross || data.summary?.total_salary_basic || 0).toLocaleString("en-US")} ر.س`);
      setFile(null);
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل التحميل");
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ padding: 14, marginTop: 14 }} data-testid="employees-upload-card">
      <div style={{ fontSize: 13.5, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
        👥 رفع قائمة الموظفين (Excel)
      </div>
      <p style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10, lineHeight: 1.7 }}>
        ارفع ملف Excel بصيغة قالب «قائمة الموظفين» (38 عمود). سيتم استخدام بياناتهم تلقائياً في كل
        صفحات التطبيق: نماذج إنشاء المستخدمين، إجمالي الرواتب في الإدارة المالية، إحصاءات الفروع.
      </p>
      {summary && summary.headcount > 0 && (
        <div style={{ background: "rgba(212,175,55,0.06)", padding: 10, borderRadius: 8, marginBottom: 10, fontSize: 11.5, color: "var(--brown-deep)" }} data-testid="employees-current">
          📊 محمّل حالياً: <b>{summary.headcount}</b> موظف · رواتب أساسية {Number(summary.total_salary_basic || 0).toLocaleString("en-US")} ر.س
          {summary.by_branch?.length > 0 && <span style={{ color: "var(--muted)", marginInlineStart: 8 }}>· {summary.by_branch.map((b) => `${b.branch}: ${b.count}`).join(" · ")}</span>}
        </div>
      )}
      <label
        htmlFor="employees-file-input"
        style={{
          display: "block",
          padding: "16px",
          background: "rgba(212,175,55,0.06)",
          border: "1.5px dashed var(--gold)",
          borderRadius: 10,
          textAlign: "center",
          cursor: "pointer",
          color: "var(--brown-deep)",
          fontSize: 13.5,
          fontWeight: 600,
        }}
      >
        {file ? (
          <>
            <span style={{ fontSize: 22, display: "block", marginBottom: 4 }}>📎</span>
            <span>{file.name}</span>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, fontWeight: 500 }}>
              {Math.round((file.size || 0) / 1024)} KB · اضغط لاختيار ملف آخر
            </div>
          </>
        ) : (
          <>
            <span style={{ fontSize: 26, display: "block", marginBottom: 4 }}>📂</span>
            <span>اضغط لاختيار ملف الموظفين (Excel)</span>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, fontWeight: 500 }}>
              .xlsx / .xls · حد أقصى 10 ميجا
            </div>
          </>
        )}
      </label>
      <input
        id="employees-file-input"
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(""); setMsg(null); }}
        style={{ display: "none" }}
        data-testid="employees-file"
      />
      <button type="button" className="btn btn-primary" onClick={submit} disabled={!file || busy} data-testid="employees-submit" style={{ fontSize: 14, marginTop: 10, width: "100%", padding: "12px", fontWeight: 800 }}>
        {busy ? "⏳ يقرأ الملف…" : "📤 رفع وحفظ قائمة الموظفين"}
      </button>
      {err && <div style={{ fontSize: 11.5, color: "var(--rd, #b9533e)", marginTop: 6 }} data-testid="employees-err">{err}</div>}
      {msg && <div style={{ fontSize: 11.5, color: "var(--success, #2e8b57)", marginTop: 6 }} data-testid="employees-ok">{msg}</div>}
    </div>
  );
}

